import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Upload, FileText, Download, Trash2, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';

const Documents = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('AHPCSA');
  const [description, setDescription] = useState('');
  
  const documentTypes = ['AHPCSA', 'BHF', 'ID Document', 'Qualification Certificate', 'Other'];
  
  useEffect(() => {
    fetchDocuments();
  }, []);
  
  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/user/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please upload a file smaller than 5MB.',
        variant: 'destructive'
      });
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF, JPG, or PNG file.',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (description) {
        formData.append('description', description);
      }
      
      const response = await axios.post(
        `${backendUrl}/api/user/documents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast({
        title: 'Document uploaded',
        description: 'Your document has been uploaded successfully and is pending verification.'
      });

      // Reset form
      setDescription('');
      e.target.value = null;
      
      // Refresh documents list
      fetchDocuments();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload document. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/user/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast({
        title: 'Download started',
        description: 'Your document is being downloaded.'
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Failed to download document.',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Are you sure you want to archive "${filename}"? It can be restored later.`)) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/user/documents/${docId}`);
      
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
      
      toast({
        title: 'Document deleted',
        description: 'The document has been removed.'
      });
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Failed to delete document.',
        variant: 'destructive'
      });
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };
  
  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="MY DOCUMENTS" />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Upload Section */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900">Upload Documents</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Upload your AHPCSA certificate and other relevant documents
                </p>
              </div>
              
              <div className="space-y-4 mb-4">
                <div>
                  <Label htmlFor="document-type">Document Type</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description for this document"
                    className="mt-2"
                  />
                </div>
              </div>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG, PNG up to 5MB
                  </p>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Documents List */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Documents</h3>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                  <p className="mt-2 text-gray-600">Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-gray-600">No documents uploaded yet.</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload your documents using the form above.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div
                      key={doc.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{doc.original_filename}</p>
                            {doc.is_verified ? (
                              <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                <CheckCircle size={12} className="mr-1" />
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                                <Clock size={12} className="mr-1" />
                                Pending Verification
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {doc.document_type} • {formatFileSize(doc.file_size)} • Uploaded on {formatDate(doc.uploaded_at)}
                          </p>
                          {doc.description && (
                            <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(doc.id, doc.original_filename)}
                        >
                          <Download size={16} className="md:mr-1" />
                          <span className="hidden md:inline">Download</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(doc.id, doc.original_filename)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} className="md:mr-1" />
                          <span className="hidden md:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Documents;